import React, { useEffect, useState } from "react";
import { Sidebar, MenuItemLink, useLogout, useSidebarState } from "react-admin";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Typography,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { auth } from "./firebaseClient";
import { loadSupplierProfile } from "./supplierApi";
import logo from "./assets/comfort-logo.png";

const drawerWidth = 268;

const itemSx = {
  minHeight: 48,
  mx: 1.25,
  my: 0.25,
  px: 1.5,
  borderRadius: 2,
  "& .MuiListItemIcon-root": { minWidth: 40, color: "inherit" },
  "& .MuiListItemText-primary": { fontWeight: 500, fontSize: "0.98rem" },
  "&.Mui-selected .MuiListItemText-primary": { fontWeight: 700 },
};

export default function SupplierSidebar(props) {
  const [open] = useSidebarState();
  const logout = useLogout();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    let active = true;
    loadSupplierProfile()
      .then((data) => { if (active) setProfile(data || null); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const company = profile?.companyName || profile?.name || "Supplier Partner";
  const contactName = profile?.name || auth.currentUser?.displayName || "Supplier";
  const email = profile?.email || auth.currentUser?.email || "";
  const initial = (company || contactName || "S").trim().charAt(0).toUpperCase();

  return (
    <Sidebar
      {...props}
      sx={{
        "& .RaSidebar-fixed, & .RaSidebar-drawerPaper": {
          top: { xs: "var(--RaLayout-appBarHeight)", md: 0 },
          height: { xs: "calc(100% - var(--RaLayout-appBarHeight))", md: "100%" },
          width: { xs: "min(86vw, 320px)", md: drawerWidth },
          maxWidth: "100vw",
          boxSizing: "border-box",
          bgcolor: "background.paper",
          borderRight: "1px solid",
          borderColor: "divider",
          boxShadow: "none",
          overflowX: "hidden",
        },
      }}
    >
      <Box sx={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: open ? 2.5 : 1, pt: 3, pb: 2, textAlign: "center" }}>
          <Box
            component="img"
            src={logo}
            alt="Comfort Line Luxury"
            sx={{ width: open ? 130 : 42, maxWidth: "100%", mb: open ? 2 : 1 }}
          />

          {open && (
            <>
              <Avatar sx={{ mx: "auto", mb: 1, bgcolor: "primary.main", width: 44, height: 44 }}>
                {initial}
              </Avatar>
              <Typography fontWeight={800} noWrap title={company}>{company}</Typography>
              <Typography variant="body2" color="text.secondary" noWrap title={contactName}>{contactName}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.25, wordBreak: "break-word", lineHeight: 1.25 }}
              >
                {email}
              </Typography>
            </>
          )}
        </Box>

        <Divider />

        <Box sx={{ py: 1.5 }}>
          <MenuItemLink to="/" primaryText="Dashboard" leftIcon={<DashboardIcon />} sidebarIsOpen={open} sx={itemSx} />
          <MenuItemLink to="/new-bookings" primaryText="New Bookings" leftIcon={<MarkEmailUnreadOutlinedIcon />} sidebarIsOpen={open} sx={itemSx} />
          <MenuItemLink to="/accepted-bookings" primaryText="Accepted Bookings" leftIcon={<TaskAltIcon />} sidebarIsOpen={open} sx={itemSx} />
          <MenuItemLink to="/active-rides" primaryText="Active Rides" leftIcon={<DirectionsCarFilledOutlinedIcon />} sidebarIsOpen={open} sx={itemSx} />
          <MenuItemLink to="/completed-rides" primaryText="Completed Rides" leftIcon={<HistoryIcon />} sidebarIsOpen={open} sx={itemSx} />
          <MenuItemLink to="/my-drivers" primaryText="My Drivers & Cars" leftIcon={<PeopleAltOutlinedIcon />} sidebarIsOpen={open} sx={itemSx} />
        </Box>

        <Box sx={{ mt: "auto", p: open ? 2 : 1 }}>
          <Divider sx={{ mb: 2 }} />
          {open ? (
            <>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={() => logout()}
                sx={{ color: "primary.main", borderColor: "rgba(211,47,47,0.55)" }}
              >
                Logout
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                textAlign="center"
                sx={{ mt: 1.5 }}
              >
                Comfort Line Luxury Supplier Portal
              </Typography>
            </>
          ) : (
            <Button fullWidth onClick={() => logout()} sx={{ minWidth: 0, p: 1 }} aria-label="Logout">
              <LogoutIcon />
            </Button>
          )}
        </Box>
      </Box>
    </Sidebar>
  );
}
